import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GCsByProduct } from '../../types';

import {
  reportFilterCountryCodeSelector,
  reportFilterDiscountsJoinSelector,
  reportGCsByProductSelector,
  reportGcsFilterSelector,
  reportMissingClosingTotalMissingClosingTotalSelector,
  reportSalesTypesSelector,
} from '../../state';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';
import handlerFactory, { GCsByProductDataGridContext } from './handlerFactory';

const DataGridTitle = () => {
  const { t } = useTranslation();
  const { loaded, data: totals } = useSelector(
    reportMissingClosingTotalMissingClosingTotalSelector
  );
  let title = t('gcs-by-product.title');

  if (loaded && totals !== null) {
    title += ` (${totals} ${t('sale-totals.labels.missing')})`;
  }
  return <span>{title}</span>;
};

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<GCsByProduct, GCsByProductDataGridContext>();

  const { t } = useTranslation();
  const titles = useMemo(
    () => ({ title: t('gcs-by-product.title'), tableTitle: <DataGridTitle /> }),
    [t]
  );
  const country = useSelector(reportFilterCountryCodeSelector);
  const { data: saleTypeOptions } = useSelector(reportSalesTypesSelector);
  const discountsJoin = useSelector(reportFilterDiscountsJoinSelector);
  const reportGcsFilters = useSelector(reportGcsFilterSelector);

  const { loading, data } = useSelector(reportGCsByProductSelector);

  const gridContext = useMemo(
    () => ({
      t,
      country,
      saleTypeOptions,
      discountsJoin,
    }),
    [t, country, saleTypeOptions, discountsJoin]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: titles,
    loading,
    filters: reportGcsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<GCsByProduct, GCsByProductDataGridContext>();

  useEffect(() => {
    handler.setExportConfig({
      XLSX: { includeTotals: false },
      PDF: { includeTotals: false },
    });
  }, [handler]);

  return null;
};

const GCsByProductDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory} hideSummaryRow>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default GCsByProductDataGrid;
